import React, {useRef, useEffect} from "react";
import {useModel23Query} from "../query";
import LoadingOverlay from "../../../../components/LoadingOverlay";
import YetizSnowProfile, {
    YetizSnowProfileContainer,
    YetizSnowProfileTools,
    YetizSnowProfileZoomWrapper,
    useYetizSnowProfileMenu,
    useYetizSnowProfileProperties,
    useYetizSnowProfileZoom
} from "../../../../components/Graph/YetizSnowProfile";
import MDBox from "../../../../materialDashboard/components/MDBox";
import {Checkbox, FormControlLabel, Typography} from "@mui/material";
import Stack from "@mui/material/Stack";
import {useTranslation} from "react-i18next";
import useActiveFeature from "./useActiveFeature";
import FeatureSelectionChips from "./FeatureSelectionChips";
import {format} from "date-fns";

const formatDateTime = (dateTime) => {
    const date = new Date(dateTime);
    return format(date, "dd/MM/yyyy HH:mm")
};

const Model23Panel = ({selectedFeatures, onDeSelectFeature}) => {
    const {t} = useTranslation(['webgis']);
    const containerRef = useRef(null);

    const {activeFeatureId, setActiveFeatureId} = useActiveFeature(selectedFeatures);

    const model23Query = useModel23Query(activeFeatureId, {enabled: activeFeatureId !== null});

    const {graphRef, handleDownloadPDF, handleDownloadPNG} = useYetizSnowProfileMenu({model23: model23Query.data});
    const {
        showRamm,
        toggleRamm,
        showStratigraphy,
        toggleStratigraphy,
        showCrystals,
        toggleCrystals,
        showLayerComments,
        toggleLayerComments,
        showFlags,
        toggleFlags,
        yetizProperties,
    } = useYetizSnowProfileProperties();

    const yetizSnowProfileZoom = useYetizSnowProfileZoom({initialZoom: 0.7});

    useEffect(() => {
        const updateZoom = () => {
            if (containerRef.current) {
                const width = containerRef.current.offsetWidth;
                if (width > 0) {
                    const targetWidth = width - 48;
                    const computedZoom = targetWidth / 1000;
                    yetizSnowProfileZoom.setZoom(computedZoom);
                }
            }
        };

        if (model23Query.data && containerRef.current) {
            updateZoom();

            const observer = new ResizeObserver(() => {
                updateZoom();
            });

            observer.observe(containerRef.current);

            return () => {
                observer.disconnect();
            };
        }
    }, [model23Query.data, yetizSnowProfileZoom.setZoom]);

    if (!activeFeatureId) {
        return null;
    }

    if (model23Query.isLoading) {
        return <LoadingOverlay/>;
    }

    return (
        <MDBox ref={containerRef}>
            <FeatureSelectionChips
                selectedFeatures={selectedFeatures}
                activeFeatureId={activeFeatureId}
                setActiveFeatureId={setActiveFeatureId}
                onDeSelectFeature={onDeSelectFeature}
                getLabel={(feature) => `${feature.properties.station?.code || feature.properties.id} - ${formatDateTime(feature.properties.datetime)}`}
                sx={{ mb: 2 }}
            />

            <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ mb: 2 }}>
                <YetizSnowProfileTools
                    onDownloadPDF={handleDownloadPDF}
                    onDownloadPNG={handleDownloadPNG}
                />
            </Stack>

            <MDBox sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <FormControlLabel
                    control={<Checkbox checked={showStratigraphy} onChange={toggleStratigraphy} color="info" size="small" />}
                    label={<Typography variant="caption">{t('model23Panel.showStratigraphy')}</Typography>}
                />
                <FormControlLabel
                    control={<Checkbox checked={showRamm} onChange={toggleRamm} color="info" size="small" />}
                    label={<Typography variant="caption">{t('model23Panel.showRamm')}</Typography>}
                />
                <FormControlLabel
                    control={<Checkbox checked={showCrystals} onChange={toggleCrystals} color="info" size="small" />}
                    label={<Typography variant="caption">{t('model23Panel.showCrystals')}</Typography>}
                />
                <FormControlLabel
                    control={<Checkbox checked={showLayerComments} onChange={toggleLayerComments} color="info" size="small" />}
                    label={<Typography variant="caption">{t('model23Panel.showLayerComments')}</Typography>}
                />
                <FormControlLabel
                    control={<Checkbox checked={showFlags} onChange={toggleFlags} color="info" size="small" />}
                    label={<Typography variant="caption">{t('model23Panel.showFlags')}</Typography>}
                />
            </MDBox>

            <YetizSnowProfileContainer width={yetizSnowProfileZoom.width} height={yetizSnowProfileZoom.height} sx={{ m: 0 }}>
                <YetizSnowProfileZoomWrapper zoom={yetizSnowProfileZoom.zoom}>
                    <YetizSnowProfile
                        profile={model23Query.data}
                        yetizProperties={yetizProperties}
                        graphRef={graphRef}
                    />
                </YetizSnowProfileZoomWrapper>
            </YetizSnowProfileContainer>
        </MDBox>
    );
};

export default Model23Panel;
